import { useState, useRef, useEffect } from 'react';
import { MdSearch, MdPerson, MdLocationOn } from 'react-icons/md';
import { InputText } from 'primereact/inputtext';
import clsx from 'clsx';
import searchBarStyles from './search-bar.module.css';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { GenericResponse, ListResponse } from '@/common/common_utils';

interface ActivityAutocompleteResponse {
	SearchMatchType: string;
	// For Location matches
	Address?: string | null;
	// For Name matches
	FullName?: string | null;
	Email?: string | null;
}

interface ActivitySearchAutocompleteProps {
	onSelect: (text: string) => void;
	placeholder?: string;
	className?: string;
	value?: string;
	onChange?: (value: string) => void;
}

const ActivitySearchAutocomplete = ({
	onSelect,
	placeholder = 'Search by name or location',
	className,
	value: controlledValue,
	onChange: controlledOnChange,
}: ActivitySearchAutocompleteProps) => {
	const [internalValue, setInternalValue] = useState('');
	const [showDropdown, setShowDropdown] = useState(false);
	const [filteredResults, setFilteredResults] = useState<ActivityAutocompleteResponse[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const justSelectedRef = useRef(false);
	const containerRef = useRef<HTMLDivElement>(null);

	// Use controlled value if provided, otherwise use internal state
	const searchValue = controlledValue !== undefined ? controlledValue : internalValue;
	const setSearchValue = (value: string) => {
		if (controlledValue !== undefined && controlledOnChange) {
			controlledOnChange(value);
		} else {
			setInternalValue(value);
		}
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setShowDropdown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Fetch autocomplete results from backend
	useEffect(() => {
		const fetchResults = async () => {
			if (!searchValue.trim()) {
				setFilteredResults([]);
				setShowDropdown(false);
				return;
			}

			// Don't fetch if we just selected an item
			if (justSelectedRef.current) {
				justSelectedRef.current = false;
				return;
			}

			setIsLoading(true);
			try {
				const result = await axiosInstance.post<GenericResponse<ListResponse<ActivityAutocompleteResponse>>>(
					ENDPOINTS['/api/X4bActivity/GetActivityAutocomplete'],
					JSON.stringify(searchValue),
					{
						headers: {
							'Content-Type': 'application/json'
						}
					}
				);

				const items = result.data.Data.Items;
				setFilteredResults(items);
				setShowDropdown(items.length > 0);
			} catch (error) {
				console.error('Failed to fetch autocomplete results:', error);
				setFilteredResults([]);
				setShowDropdown(false);
			} finally {
				setIsLoading(false);
			}
		};

		// Debounce the API call
		const timeoutId = setTimeout(fetchResults, 300);
		return () => clearTimeout(timeoutId);
	}, [searchValue]);

	const handleSelect = (activity: ActivityAutocompleteResponse) => {
		// Set the search value to the selected item's display text
		const displayText = activity.SearchMatchType === 'Name'
			? activity.FullName || ''
			: activity.Address || '';

		justSelectedRef.current = true;
		setSearchValue(displayText);
		setShowDropdown(false);
		onSelect(displayText);
	};

	const getMatchIcon = (activity: ActivityAutocompleteResponse) => {
		// Use SearchMatchType from backend
		if (activity.SearchMatchType === 'Name') {
			return <MdPerson className="text-onSurfaceVariant flex-shrink-0" size={24} />;
		}
		return <MdLocationOn className="text-onSurfaceVariant flex-shrink-0" size={24} />;
	};

	const renderResultItem = (activity: ActivityAutocompleteResponse) => {
		// Use SearchMatchType from backend to determine what to display
		if (activity.SearchMatchType === 'Name') {
			// Show passenger name and email
			const fullName = activity.FullName || '';
			const contactInfo = activity.Email || '';

			return (
				<div className="flex flex-col">
					<span className="text-body-large text-onSurface font-poppins">{fullName}</span>
					{contactInfo && (
						<span className="text-body-medium text-onSurfaceVariant font-poppins">{contactInfo}</span>
					)}
				</div>
			);
		}

		// Show location address
		const address = activity.Address || '';

		return (
			<div className="flex flex-col min-w-0">
				<span className="text-body-large text-onSurface font-poppins truncate">{address}</span>
			</div>
		);
	};

	return (
		<div
			ref={containerRef}
			className={clsx(
				searchBarStyles.searchbarContainer,
				'bg-surfaceContainerHigh rounded-[28px] relative',
				showDropdown && 'rounded-b-none',
				className
			)}
		>
			<MdSearch className={searchBarStyles.searchbarIcon} />
			<InputText
				unstyled
				value={searchValue}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					const newValue = e.target.value;
					setSearchValue(newValue);
					// If input is cleared, notify parent to clear the filter
					if (newValue.trim() === '') {
						onSelect('');
					}
				}}
				placeholder={placeholder}
				className={clsx(searchBarStyles.searchbarInput)}
				style={{ backgroundImage: 'none' }}
			/>

			{showDropdown && filteredResults.length > 0 && (
				<div className={clsx(
					'absolute top-full left-0 w-full bg-surfaceContainerHigh',
					'flex flex-col rounded-b-[28px] shadow-lg border-t',
					'max-h-[400px] overflow-y-auto z-50'
				)}>
					{filteredResults.map((activity, index) => (
						<button
							key={`${activity.SearchMatchType}-${activity.Address || activity.FullName}-${index}`}
							type="button"
							onClick={() => handleSelect(activity)}
							className={clsx(
								'flex items-start gap-3 px-4 py-3',
								'hover:bg-surfaceContainerHighest cursor-pointer',
								'border-none bg-transparent text-left w-full',
								'transition-colors duration-150'
							)}
						>
							<div className="mt-1">
								{getMatchIcon(activity)}
							</div>
							{renderResultItem(activity)}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default ActivitySearchAutocomplete;

